import React, { useEffect, useMemo, useRef, useState } from "react";
import { useModalManager } from "shared/hooks/useModalManager";
import EditProductsModal from "features/products/components/EditProductModal";
import DeleteProductModal from "features/products/components/DeleteProductModal";
import DiscountProductModal from "./components/DiscountProductModal";
import BonusProductModal from "./components/BonusProductModal";
import Toolbar from "./components/DiscountToolbar";
import ProductTable from "./components/DiscountProductTable";
import ScrollToTopButton from "shared/ui/scrolltotop";
import EmptyStateComponent from "shared/ui/empty";
import { deleteProduct, updateProduct } from "features/products/api/productsApi";
import { getMerchantCategories } from "features/categories/api/categoriesApi";
import { getMerchantDiscounts } from "./api/discountsApi";
import { toast } from "react-toastify";
import { useScrollToTop } from "shared/hooks/useScrollToTop";

export default function MerchantAddDiscountScreen() {
  const { closeModal, currentModal, openModal } = useModalManager();
  const { showScrollButton, scrollToTop } = useScrollToTop();

  // ✅ edit шиг: raw + filtered
  const [allItems, setAllItems] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  // filters
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null); // parent
  const [selectedSubCategory, setSelectedSubCategory] = useState(null); // child

  // categories
  const [rawCategories, setRawCategories] = useState([]);
  const [categories, setCategories] = useState([
    { value: null, label: "Бүх ангилал" },
  ]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  // modals
  const [editingProduct, setEditingProduct] = useState(null);
  const [discountingProduct, setDiscountingProduct] = useState(null);
  const [bonusProduct, setBonusProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);

  const [loadingProductId, setLoadingProductId] = useState(null);
  const [saving, setSaving] = useState(false); // (хэрэв modal дотор save байхгүй бол устгаж болно)
  const [deleting, setDeleting] = useState(false);

  // ✅ хуучин request state-г даруулахгүй
  const requestIdRef = useRef(0);

  const PAGE_SIZE = 32;

  /* -------------------- Categories -------------------- */
  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      setCategoriesError(null);

      const res = await getMerchantCategories();
      // res: {success, data}
      const tree = Array.isArray(res?.data) ? res.data : [];

      setRawCategories(tree);

      const options = tree.map((cat) => ({
        value: String(cat.id),
        label: cat.name,
      }));

      setCategories([{ value: null, label: "Бүх ангилал" }, ...options]);
    } catch (e) {
      console.error("loadCategories error:", e);
      setCategoriesError("Ангилал татахад алдаа гарлаа.");
      setRawCategories([]);
      setCategories([{ value: null, label: "Бүх ангилал" }]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // ✅ parent сонгосон үед front filter хийх id-ууд: parent + children
  const parentFilterIds = useMemo(() => {
    if (!selectedCategory) return null;

    const parent = rawCategories.find(
      (p) => String(p.id) === String(selectedCategory)
    );

    if (!parent) return [Number(selectedCategory)];

    const childrenIds = (parent.children || []).map((c) => Number(c.id));
    return [Number(parent.id), ...childrenIds];
  }, [selectedCategory, rawCategories]);

  const applyFrontFilter = (list) => {
    // ✅ sub сонгосон үед backend filter ашиглана (front filter хийхгүй)
    if (selectedSubCategory) return list;
    if (!selectedCategory) return list;

    const ids = parentFilterIds || [];
    return (list || []).filter((p) => ids.includes(Number(p.categoryId)));
  };

  /* -------------------- Load Products -------------------- */
  const loadProducts = async (pageToLoad = 1, isRefresh = false) => {
    const reqId = ++requestIdRef.current;

    try {
      setLoading(true);

      const limit = PAGE_SIZE;
      const offset = (Number(pageToLoad) - 1) * limit;

      // ✅ sub сонгосон үед backend-д categoryId=subId явуулна
      const categoryIdForBackend =
        selectedSubCategory !== null && selectedSubCategory !== undefined
          ? Number(selectedSubCategory)
          : null;

      // ✅ Backend-д яг юу хэрэгтэйг тодорхойгүй үед хамгийн найдвартай нь:
      // - page/pageSize болон limit/offset хоёуланг явуулна
      // - keyword/keyWord хоёуланг явуулна
      const body = {
        page: Number(pageToLoad),
        pageSize: limit,

        limit,
        offset,

        categoryId:
          categoryIdForBackend !== null && Number.isFinite(categoryIdForBackend)
            ? categoryIdForBackend
            : null,

        keyword: (search || "").trim(),
        keyWord: (search || "").trim(),
      };

      const result = await getMerchantDiscounts(body);

      // ✅ хуучин хүсэлт бол state бүү дар
      if (reqId !== requestIdRef.current) return;

      // result хэлбэр: AxiosRequest -> ихэнхдээ {success, data, pagination}
      const rows = Array.isArray(result?.data) ? result.data : [];
      const pagination = result?.pagination || null;

      const mapped = rows.map((p) => {
        const discount = p.discount || {};
        const bonus = p.bonus || {};

        const price = Number(p.price ?? 0);

        // discount.discountValue-г танай UI "хямд үнэ" гэж үздэгээр тооцлоо
        const discountPrice =
          discount.discountValue !== null &&
          discount.discountValue !== undefined
            ? Number(discount.discountValue)
            : null;

        const discountIsActive = Number(discount.isActive) === 1;

        const discountPercent =
          discountIsActive && price > 0 && discountPrice !== null
            ? Math.round(((price - discountPrice) / price) * 100)
            : null;

        const bonusItems = Array.isArray(bonus.items) ? bonus.items : [];

        return {
          id: p.id,
          name: p.name,
          image: p.image,
          price,
          quantity: p.quantity,
          isVisible: p.isVisible,
          categoryId: p.categoryId, // ✅ front filter-д хэрэгтэй

          discountPrice,
          discountPercent,
          discountIsActive,
          discountStartAt: discount.startAt,
          discountEndAt: discount.endAt,

          bonusCount: bonusItems.length,
          bonusRuleId: bonus.id,
          bonusItems,
        };
      });

      if (isRefresh) {
        setAllItems(mapped);
        setItems(applyFrontFilter(mapped));
      } else {
        setAllItems((prev) => {
          const merged = [...prev, ...mapped];
          setItems(applyFrontFilter(merged));
          return merged;
        });
      }

      // pagination байхгүй байж магадгүй → hasMore-г rows length-р тооцоолно
      if (pagination?.currentPage && pagination?.totalPages) {
        setHasMore(pagination.currentPage < pagination.totalPages);
        setPage(pagination.currentPage);
      } else {
        setHasMore(mapped.length >= limit);
        setPage(Number(pageToLoad));
      }
    } catch (e) {
      console.error("loadProducts error:", e);
      toast.error("Бараа татахад алдаа гарлаа.");
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  /* -------------------- Effects -------------------- */
  useEffect(() => {
    loadCategories();
    loadProducts(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ search + sub өөрчлөгдвөл backend refresh
  useEffect(() => {
    setAllItems([]);
    setItems([]);
    loadProducts(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedSubCategory]);

  // ✅ parent өөрчлөгдвөл backend татахгүй, зөвхөн front filter
  useEffect(() => {
    setItems(applyFrontFilter(allItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, parentFilterIds]);

  /* -------------------- Handlers -------------------- */
  const handleLoadMore = () => {
    if (hasMore && !loading) loadProducts(page + 1, false);
  };

  const handleRefetch = () => {
    setAllItems([]);
    setItems([]);
    loadProducts(1, true);
  };

  /* Бараа засах — EditProductsModal-д `onSave` дамжуулаагүй тул «Хадгалах»
     дарахад `onSave is not a function` CRASH гардаг байв. updateProduct-оор
     зөв хадгалж, жагсаалтаа дахин татна. */
  const handleSaveProduct = async (updatedData) => {
    if (!editingProduct?.id) {
      toast.error("Барааны ID олдсонгүй.");
      return;
    }
    try {
      setSaving(true);
      const res = await updateProduct(editingProduct.id, updatedData);
      if (res?.success === false) throw new Error(res?.message || "Алдаа");
      toast.success(res?.message || "Бараа амжилттай шинэчлэгдлээ.");
      handleRefetch();
    } catch (err) {
      console.error("discount handleSaveProduct error:", err);
      toast.error("Барааг шинэчлэх үед алдаа гарлаа.");
    } finally {
      setSaving(false);
      closeModal();
      setEditingProduct(null);
    }
  };

  const handleVisible = async (e, productId) => {
    const newVisible = e.target.checked;
    try {
      setLoadingProductId(productId);
      await updateProduct(productId, { isVisible: newVisible });

      setAllItems((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, isVisible: newVisible } : p
        )
      );
      setItems((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, isVisible: newVisible } : p
        )
      );

      toast.success("Бараа амжилттай шинэчлэгдлээ.");
    } catch (err) {
      console.error(err);
      toast.error("Барааг шинэчлэх үед алдаа гарлаа.");
    } finally {
      setLoadingProductId(null);
    }
  };

  const isEmpty = !loading && items.length === 0;

  /* -------------------- Render -------------------- */
  return (
    <div className="container-fluid app">
      <div className="row">
        <div className="col-12">
          <Toolbar
            search={search}
            setSearch={setSearch}
            selectedCategory={selectedCategory}
            handleCategoryChange={(v) => {
              setSelectedCategory(v);
              setSelectedSubCategory(null); // ✅ parent солигдвол sub reset
            }}
            selectedSubCategory={selectedSubCategory}
            handleSubCategoryChange={(v) => setSelectedSubCategory(v)}
            categories={categories}
            rawCategories={rawCategories}
            loadingCategories={loadingCategories}
            categoriesError={categoriesError}
            onRefetch={handleRefetch}
          />

          <h4 className="page-title mt-3">Хямдрал / Урамшуулал</h4>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body pt-1">
              {isEmpty ? (
                <EmptyStateComponent
                  title={"Бараа байхгүй байна"}
                  subtitle={"Та одоогоор ямар нэг бараа бүртгээгүй байна"}
                />
              ) : (
                <ProductTable
                  items={items}
                  loading={loading}
                  hasMore={hasMore}
                  onEndReached={handleLoadMore}
                  loadingProductId={loadingProductId}
                  handleVisible={handleVisible}
                  onEdit={(p) => {
                    setEditingProduct(p);
                    openModal("edit-product");
                  }}
                  onDelete={(p) => {
                    setDeletingProduct(p);
                    openModal("delete-product");
                  }}
                  onDiscount={(p) => {
                    setDiscountingProduct(p);
                    openModal("discount-product");
                  }}
                  onBonus={(p) => {
                    setBonusProduct(p);
                    openModal("bonus-product");
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <EditProductsModal
        show={currentModal === "edit-product"}
        handleClose={() => {
          closeModal();
          setEditingProduct(null);
        }}
        initialData={editingProduct || {}}
        categories={rawCategories}
        onSave={handleSaveProduct}
        saving={saving}
      />

      <DeleteProductModal
        show={currentModal === "delete-product"}
        handleClose={() => {
          closeModal();
          setDeletingProduct(null);
        }}
        product={deletingProduct || {}}
        onDelete={async () => {
          if (!deletingProduct?.id) return;

          try {
            setDeleting(true);
            await deleteProduct(deletingProduct.id);

            setAllItems((prev) =>
              prev.filter((x) => x.id !== deletingProduct.id)
            );
            setItems((prev) => prev.filter((x) => x.id !== deletingProduct.id));

            toast.success("Бараа амжилттай устгагдлаа.");
            closeModal();
            setDeletingProduct(null);
          } catch (e) {
            console.error(e);
            toast.error("Барааг устгах үед алдаа гарлаа.");
          } finally {
            setDeleting(false);
          }
        }}
        deleting={deleting}
      />

      <DiscountProductModal
        show={currentModal === "discount-product"}
        handleClose={() => {
          closeModal();
          setDiscountingProduct(null);
        }}
        product={discountingProduct || {}}
        onUpdated={() => {
          closeModal();
          setDiscountingProduct(null);
          handleRefetch();
        }}
      />

      <BonusProductModal
        show={currentModal === "bonus-product"}
        handleClose={() => {
          closeModal();
          setBonusProduct(null);
        }}
        product={bonusProduct || {}}
        onSuccess={() => {
          closeModal();
          setBonusProduct(null);
          handleRefetch();
        }}
      />

      {showScrollButton && <ScrollToTopButton onClick={scrollToTop} />}
    </div>
  );
}
